import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { extractErrorMessage } from '../../../api/errors';
import { marketplaceApi } from '../../../api/marketplace';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../context/AuthContext';
import { useCart } from '../buyer/CartContext';

function formatCurrency(n) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

const DOC_TYPES = [
  { value: 'lab_report', label: 'Lab analysis report' },
  { value: 'organic_cert', label: 'Organic certification' },
  { value: 'fssai', label: 'FSSAI license' },
  { value: 'weighbridge', label: 'Weighbridge slip' },
  { value: 'quality_sheet', label: 'Quality data sheet' },
  { value: 'other', label: 'Other' },
];

const PROGRESS_STAGES = [
  { value: 'sown', label: 'Sown / planted', icon: '🌱' },
  { value: 'germinated', label: 'Germinated', icon: '🌿' },
  { value: 'vegetative', label: 'Vegetative growth', icon: '🌾' },
  { value: 'flowering', label: 'Flowering', icon: '🌸' },
  { value: 'fruiting', label: 'Fruiting / pod set', icon: '🍅' },
  { value: 'harvested', label: 'Harvested', icon: '👨‍🌾' },
  { value: 'sorted', label: 'Sorted / graded', icon: '🧺' },
  { value: 'packed', label: 'Packed', icon: '📦' },
  { value: 'dispatched', label: 'Dispatched', icon: '🚚' },
  { value: 'other', label: 'Other', icon: '◆' },
];

const SENSOR_METRICS = [
  { value: 'soil_moisture', label: 'Soil moisture', unit: '%' },
  { value: 'air_temp', label: 'Air temperature', unit: '°C' },
  { value: 'soil_temp', label: 'Soil temperature', unit: '°C' },
  { value: 'humidity', label: 'Humidity', unit: '%' },
  { value: 'ph', label: 'pH', unit: '' },
  { value: 'npk_n', label: 'Nitrogen', unit: 'ppm' },
  { value: 'npk_p', label: 'Phosphorus', unit: 'ppm' },
  { value: 'npk_k', label: 'Potassium', unit: 'ppm' },
  { value: 'light', label: 'Light', unit: 'lux' },
  { value: 'other', label: 'Other', unit: '' },
];

function iconForStage(stage) {
  const s = PROGRESS_STAGES.find((s) => s.value === stage);
  return s ? s.icon : '◆';
}

function ProgressTimeline({ events, isOwnerAdmin, listingId, onChange }) {
  if (!isOwnerAdmin && (!events || events.length === 0)) return null;
  return (
    <Card className="p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Crop journey</h2>
      <p className="mt-1 text-xs text-gray-500">
        Stage-by-stage record of the crop, logged by the FPO.
      </p>

      {events && events.length > 0 ? (
        <ol className="relative mt-5 ml-3 border-l-2 border-green-100">
          {events.map((ev) => (
            <li key={ev.id} className="ml-6 py-3">
              <span className="absolute -left-[14px] flex h-6 w-6 items-center justify-center rounded-full bg-green-700 text-xs">
                {iconForStage(ev.stage)}
              </span>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900">{ev.stage_display}</p>
                <p className="text-xs text-gray-500">{ev.occurred_on}</p>
              </div>
              {ev.notes && (
                <p className="mt-1 text-xs leading-relaxed text-gray-600">{ev.notes}</p>
              )}
              {ev.photo && (
                <img
                  src={ev.photo}
                  alt={ev.stage_display}
                  className="mt-2 h-28 w-auto rounded-md object-cover"
                />
              )}
              {isOwnerAdmin && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Remove "${ev.stage_display}" event?`)) return;
                    try {
                      await marketplaceApi.deleteProgressEvent(ev.id);
                      await onChange();
                    } catch (err) {
                      window.alert(extractErrorMessage(err));
                    }
                  }}
                  className="mt-1 text-[11px] font-semibold text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-4 text-xs italic text-gray-400">No journey logged yet.</p>
      )}

      {isOwnerAdmin && <ProgressLogger listingId={listingId} onLogged={onChange} />}
    </Card>
  );
}

function ProgressLogger({ listingId, onLogged }) {
  const today = new Date().toISOString().slice(0, 10);
  const [stage, setStage] = useState('sown');
  const [occurredOn, setOccurredOn] = useState(today);
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await marketplaceApi.addProgressEvent(listingId, { stage, occurredOn, notes, photo });
      setNotes('');
      setPhoto(null);
      if (e.target.querySelector('input[type=file]')) {
        e.target.querySelector('input[type=file]').value = '';
      }
      await onLogged();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="mt-5 flex flex-col gap-3 border-t border-gray-200 pt-4" onSubmit={handleSubmit}>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Log a stage</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Stage</label>
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
          >
            {PROGRESS_STAGES.map((s) => (
              <option key={s.value} value={s.value}>{s.icon}  {s.label}</option>
            ))}
          </select>
        </div>
        <Input
          label="Date"
          type="date"
          value={occurredOn}
          onChange={(e) => setOccurredOn(e.target.value)}
          required
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">Notes (optional)</label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
          placeholder="Drip irrigation started. Crop looking healthy."
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">Photo (optional)</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setPhoto(e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-green-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-green-700 hover:file:bg-green-100"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <Button type="submit" disabled={submitting} className="self-start">
        {submitting ? 'Saving…' : 'Log this stage'}
      </Button>
    </form>
  );
}

function SensorPanel({ readings, isOwnerAdmin, listingId, onChange }) {
  if (!isOwnerAdmin && (!readings || readings.length === 0)) return null;
  return (
    <Card className="p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        Latest sensor readings
      </h2>
      <p className="mt-1 text-xs text-gray-500">
        Most recent value per metric. Useful for buyers verifying moisture, pH, or NPK at source.
      </p>

      {readings && readings.length > 0 ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {readings.map((r) => (
            <div
              key={r.id}
              className="rounded-lg border border-gray-200 bg-gradient-to-br from-sky-50 to-white p-3"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-700">
                {r.metric_display}
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {Number(r.value).toFixed(2)}
                <span className="ml-1 text-sm font-normal text-gray-500">{r.unit || ''}</span>
              </p>
              <p className="mt-1 text-[11px] text-gray-500">
                {new Date(r.recorded_at).toLocaleString()}
                {r.source ? ` · ${r.source}` : ''}
              </p>
              {isOwnerAdmin && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Remove this reading?')) return;
                    try {
                      await marketplaceApi.deleteSensorReading(r.id);
                      await onChange();
                    } catch (err) {
                      window.alert(extractErrorMessage(err));
                    }
                  }}
                  className="mt-1 text-[11px] font-semibold text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-xs italic text-gray-400">No readings yet.</p>
      )}

      {isOwnerAdmin && <SensorLogger listingId={listingId} onLogged={onChange} />}
    </Card>
  );
}

function SensorLogger({ listingId, onLogged }) {
  const nowIso = new Date().toISOString().slice(0, 16);
  const [metric, setMetric] = useState('soil_moisture');
  const [value, setValue] = useState('');
  const [recordedAt, setRecordedAt] = useState(nowIso);
  const [source, setSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const m = SENSOR_METRICS.find((x) => x.value === metric);
      await marketplaceApi.addSensorReading(listingId, {
        metric,
        value,
        unit: m ? m.unit : '',
        recorded_at: new Date(recordedAt).toISOString(),
        source,
      });
      setValue('');
      setSource('');
      await onLogged();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="mt-5 flex flex-col gap-3 border-t border-gray-200 pt-4" onSubmit={handleSubmit}>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Add a reading</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Metric</label>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
          >
            {SENSOR_METRICS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}{m.unit ? ` (${m.unit})` : ''}
              </option>
            ))}
          </select>
        </div>
        <Input
          label="Value"
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          placeholder="12.5"
        />
        <Input
          label="Recorded at"
          type="datetime-local"
          value={recordedAt}
          onChange={(e) => setRecordedAt(e.target.value)}
          required
        />
        <Input
          label="Source (sensor / agronomist / lab)"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="Field sensor #3"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <Button type="submit" disabled={submitting} className="self-start">
        {submitting ? 'Saving…' : 'Log reading'}
      </Button>
    </form>
  );
}

function QualityBadge({ grade, gradeDisplay, moisturePct }) {
  if (!grade && moisturePct == null) return null;
  const tone =
    grade === 'A' || grade === 'organic'
      ? 'bg-green-100 text-green-800 ring-green-200'
      : grade === 'FAQ' || grade === 'B'
        ? 'bg-amber-100 text-amber-800 ring-amber-200'
        : 'bg-gray-100 text-gray-700 ring-gray-200';
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {grade && (
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${tone}`}>
          ✦ {gradeDisplay || grade}
        </span>
      )}
      {moisturePct != null && moisturePct !== '' && (
        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800 ring-1 ring-sky-200">
          Moisture {Number(moisturePct).toFixed(1)}%
        </span>
      )}
    </div>
  );
}

function DocumentsPanel({ documents, isOwnerAdmin, listingId, onChange }) {
  if (!isOwnerAdmin && (!documents || documents.length === 0)) return null;
  return (
    <Card className="p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        Quality & compliance docs
      </h2>
      <p className="mt-1 text-xs text-gray-500">
        Lab reports, certifications, and other paperwork buyers can verify.
      </p>

      {documents && documents.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2">
          {documents.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <a
                  href={d.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-sm font-semibold text-green-800 hover:text-green-900"
                >
                  {d.label || d.doc_type_display}
                </a>
                <p className="text-xs text-gray-500">{d.doc_type_display}</p>
              </div>
              {isOwnerAdmin && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Remove "${d.label || d.doc_type_display}"?`)) return;
                    try {
                      await marketplaceApi.deleteListingDocument(d.id);
                      await onChange();
                    } catch (err) {
                      window.alert(extractErrorMessage(err));
                    }
                  }}
                  className="text-xs font-semibold text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-xs italic text-gray-400">No docs uploaded yet.</p>
      )}

      {isOwnerAdmin && <DocumentUploader listingId={listingId} onUploaded={onChange} />}
    </Card>
  );
}

function DocumentUploader({ listingId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('other');
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await marketplaceApi.uploadListingDocument(listingId, file, { docType, label });
      setFile(null);
      setLabel('');
      if (e.target.querySelector('input[type=file]')) {
        e.target.querySelector('input[type=file]').value = '';
      }
      await onUploaded();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form className="mt-5 flex flex-col gap-3 border-t border-gray-200 pt-4" onSubmit={handleUpload}>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Add a document</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Document type</label>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
          >
            {DOC_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <Input
          label="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="2026 NABL report"
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">File</label>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-green-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-green-700 hover:file:bg-green-100"
          />
        </div>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <Button type="submit" disabled={!file || uploading} className="self-start">
        {uploading ? 'Uploading…' : 'Upload document'}
      </Button>
    </form>
  );
}

function ImageGallery({ images }) {
  const [activeIdx, setActiveIdx] = useState(0);
  if (!images || images.length === 0) {
    return (
      <div className="flex h-72 w-full items-center justify-center rounded-xl bg-gray-100 text-sm text-gray-400">
        No photos yet.
      </div>
    );
  }
  const active = images[Math.min(activeIdx, images.length - 1)];
  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-xl bg-gray-100">
        <img
          src={active.image}
          alt={active.caption || 'Listing photo'}
          className="h-72 w-full object-cover sm:h-96"
        />
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto">
          {images.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              onClick={() => setActiveIdx(idx)}
              className={`h-16 w-20 flex-shrink-0 overflow-hidden rounded-md ring-2 transition ${
                idx === activeIdx ? 'ring-green-700' : 'ring-transparent hover:ring-gray-300'
              }`}
            >
              <img src={img.image} alt={img.caption || `Thumb ${idx + 1}`} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {active.caption && (
        <p className="text-xs italic text-gray-500">{active.caption}</p>
      )}
    </div>
  );
}

function ImageManager({ listingId, images, onChange }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await marketplaceApi.uploadListingImage(listingId, file, {
        caption,
        orderIndex: images.length,
      });
      setFile(null);
      setCaption('');
      if (e.target.querySelector('input[type=file]')) {
        e.target.querySelector('input[type=file]').value = '';
      }
      await onChange();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await marketplaceApi.deleteListingImage(imageId);
      await onChange();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Photos · FPO tools</h2>
      <p className="mt-1 text-xs text-gray-500">
        Add photos buyers will see — field shots, harvest, packing, certifications.
      </p>

      <form className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={handleUpload}>
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-700">Choose photo</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-green-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-green-700 hover:file:bg-green-100"
          />
        </div>
        <Input
          label="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" disabled={!file || uploading} className="mb-[1px]">
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </form>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {images.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {images.map((img) => (
            <div key={img.id} className="group relative overflow-hidden rounded-md">
              <img src={img.image} alt={img.caption || 'Photo'} className="h-24 w-full object-cover" />
              <button
                type="button"
                onClick={() => handleDelete(img.id)}
                className="absolute right-1 top-1 hidden rounded bg-red-600/90 px-2 py-0.5 text-[10px] font-semibold text-white group-hover:block"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const cart = useCart();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState('');
  const [added, setAdded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // FPO admins try their own (includes drafts + their unpublished work)
      // and fall back to the public endpoint if it's not their listing.
      let data;
      if (user.role === 'fpo_admin') {
        try {
          data = await marketplaceApi.myListing(id);
        } catch {
          data = await marketplaceApi.listing(id);
        }
      } else {
        data = await marketplaceApi.listing(id);
      }
      setListing(data);
      setQuantity((q) => q || String(data.min_order_quantity));
      setError('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <p className="text-sm text-gray-500">Loading listing…</p>;
  }

  if (error || !listing) {
    return (
      <Card className="p-8 text-center">
        <p className="text-sm text-red-600">{error || 'Listing not found.'}</p>
        <Link to="/app" className="mt-3 inline-block text-sm font-semibold text-green-700 hover:text-green-800">
          ← Back
        </Link>
      </Card>
    );
  }

  const isOwnerAdmin =
    user.role === 'fpo_admin' && user.organisation_id === listing.organisation;
  const isBuyer = user.role === 'buyer';

  const handleAdd = () => {
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      window.alert('Enter a valid quantity.');
      return;
    }
    if (qty < Number(listing.min_order_quantity)) {
      window.alert(`Minimum order is ${listing.min_order_quantity} ${listing.unit}.`);
      return;
    }
    if (qty > Number(listing.quantity_available)) {
      window.alert(`Only ${listing.quantity_available} ${listing.unit} available.`);
      return;
    }
    cart.addItem(listing, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const total = Number(quantity || 0) * Number(listing.price_per_unit);

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="self-start text-sm font-medium text-green-700 hover:text-green-800"
      >
        ← Back
      </button>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="flex flex-col gap-5">
          <ImageGallery images={listing.images} />

          <Card className="p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-green-700">
              {listing.crop_name} · {listing.crop_category}
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-900 sm:text-3xl">{listing.title}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {listing.organisation_name} · {listing.organisation_district},{' '}
              {listing.organisation_state}
            </p>

            <QualityBadge
              grade={listing.quality_grade}
              gradeDisplay={listing.quality_grade_display}
              moisturePct={listing.moisture_pct}
            />

            {listing.description && (
              <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-gray-700">
                {listing.description}
              </p>
            )}
          </Card>

          <ProgressTimeline
            events={listing.progress_events}
            isOwnerAdmin={isOwnerAdmin}
            listingId={listing.id}
            onChange={load}
          />

          <SensorPanel
            readings={listing.latest_sensor_readings}
            isOwnerAdmin={isOwnerAdmin}
            listingId={listing.id}
            onChange={load}
          />

          <DocumentsPanel
            documents={listing.documents}
            isOwnerAdmin={isOwnerAdmin}
            listingId={listing.id}
            onChange={load}
          />

          {listing.source_farms && listing.source_farms.length > 0 && (
            <Card className="p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Sourced from
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {listing.source_farms.length} member{' '}
                {listing.source_farms.length === 1 ? 'farm' : 'farms'} contributed to this listing.
              </p>
            </Card>
          )}

          {isOwnerAdmin && (
            <ImageManager listingId={listing.id} images={listing.images} onChange={load} />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Card className="p-6">
            <p className="text-xs uppercase tracking-wide text-gray-500">Price</p>
            <p className="mt-1 text-3xl font-semibold text-green-800">
              {formatCurrency(listing.price_per_unit)}
              <span className="ml-1 text-sm font-normal text-gray-500">/ {listing.unit}</span>
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Available</p>
                <p className="font-semibold text-gray-900">
                  {Number(listing.quantity_available).toFixed(2)} {listing.unit}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Min order</p>
                <p className="font-semibold text-gray-900">
                  {Number(listing.min_order_quantity).toFixed(2)} {listing.unit}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Available from</p>
                <p className="font-semibold text-gray-900">{listing.available_from}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Available until</p>
                <p className="font-semibold text-gray-900">{listing.available_until || '—'}</p>
              </div>
            </div>

            {isBuyer && (
              <div className="mt-6 flex flex-col gap-3">
                <Input
                  label={`Quantity (${listing.unit})`}
                  type="number"
                  step="0.01"
                  min={listing.min_order_quantity}
                  max={listing.quantity_available}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
                <Button onClick={handleAdd} disabled={!quantity}>
                  Add to cart · {formatCurrency(total)}
                </Button>
                {added && (
                  <p className="text-xs font-semibold text-green-700">Added to cart ✓</p>
                )}
              </div>
            )}

            {isOwnerAdmin && (
              <div className="mt-6 flex gap-2">
                <Link
                  to="/app/listings"
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-center text-sm font-semibold text-gray-700 hover:border-gray-400"
                >
                  Back to listings
                </Link>
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">FPO</h2>
            <p className="mt-1 text-base font-semibold text-gray-900">{listing.organisation_name}</p>
            <p className="text-xs text-gray-500">
              {listing.organisation_district}, {listing.organisation_state}
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
